import { useState } from 'react';
import {
  BookOpen,
  Shield,
  Users,
  AlertTriangle,
  Monitor,
  Brain,
  ArrowLeft,
  ChevronRight,
  Clock,
} from 'lucide-react';

interface DocArticle {
  id: string;
  category: string;
  tag: string;
  title: string;
  summary: string;
  readTime: string;
  icon: React.ElementType;
  content: string[];
}

const DOCS: DocArticle[] = [
  {
    id: 'getting-started',
    category: 'Getting Started',
    tag: 'Basics',
    title: 'Setting Up Your School Dashboard',
    summary:
      'A step-by-step walkthrough to get ShieldED Haven configured for your school — from inviting staff to reviewing your first safety report.',
    readTime: '4 min read',
    icon: BookOpen,
    content: [
      'Begin by navigating to School Settings and confirming your school name, grade range, and timezone are correct. These settings affect how incident timestamps and reports are displayed.',
      'Invite teachers using the Invite Tracker. Each invite generates a unique sign-up link sent via email. Teachers must complete registration before they appear in the Users list.',
      'Once teachers are active, enrol students through the Add User form. Students can be bulk-imported via CSV — download the template from the Add User page for the correct column format.',
      'After your first students are enrolled, the Chrome extension or Electron desktop app can be deployed. The extension connects automatically once a student logs in using their school credentials.',
      'Check the Dashboard 24 hours after deployment to confirm events are flowing in. The Live Activity feed should show page-visit and search-query events as students begin working.',
    ],
  },
  {
    id: 'safety-score',
    category: 'Monitoring',
    tag: 'Safety',
    title: 'Understanding the Safety Score',
    summary:
      'The safety score is a 0–100 composite metric that reflects overall content risk across your school. Learn what drives it and how to improve it.',
    readTime: '5 min read',
    icon: Shield,
    content: [
      'The score is recalculated every hour based on the past 30 days of flagged content. It weighs incident severity (critical = 4×, high = 2×, medium = 1×, low = 0.25×) against total monitored activity.',
      'A score above 80 is considered healthy. Scores between 60–80 suggest elevated risk in one or more categories. Below 60 indicates persistent issues that need active intervention.',
      'Drill into the Safety Score Breakdown page to see per-category contributions: cyberbullying, self-harm, violence, threats, profanity, and hate speech are tracked separately.',
      'Resolving open incidents improves the score. Incidents marked "resolved" within 48 hours contribute positively to the resolution-speed sub-metric.',
      'You can export a PDF safety report for the current period from the Analytics page — useful for sharing with district administrators or school board meetings.',
    ],
  },
  {
    id: 'incident-workflow',
    category: 'Incidents',
    tag: 'Workflow',
    title: 'Managing and Resolving Incidents',
    summary:
      'How the incident lifecycle works — from initial detection by the ML pipeline through teacher review, parent notification, and resolution.',
    readTime: '6 min read',
    icon: AlertTriangle,
    content: [
      'An incident is created automatically when the ML detection pipeline assigns a severity of medium or higher. Low-severity flags are surfaced as alerts but do not create incidents unless escalated manually.',
      "Each incident enters the \"Open\" state. The assigned teacher (the student's homeroom teacher) receives a push notification and email within 60 seconds of creation.",
      'Teachers review the incident in their dashboard. They can add notes, escalate severity, or mark it resolved. Escalations trigger an additional notification to you as school admin.',
      'For self-harm and threat incidents, a 15-minute self-correction window opens automatically. The student sees a prompt asking if they meant something different. Their response is logged regardless of outcome.',
      "Once an incident is resolved, the involved parties are notified. If parent notification is enabled in School Settings, a summary email is sent to the student's registered parent/guardian.",
      "All incident state transitions are written to the Audit Log with the acting user's name and timestamp — useful for compliance reporting.",
    ],
  },
  {
    id: 'live-screens',
    category: 'Monitoring',
    tag: 'Live',
    title: 'Live Screen Monitoring',
    summary:
      'Live Screens shows real-time screenshots from active student sessions. Understand the privacy controls, capture frequency, and how to act on what you see.',
    readTime: '3 min read',
    icon: Monitor,
    content: [
      'Screenshots are captured every 30 seconds on active student sessions when the Electron app is running. The browser extension alone does not capture screenshots — it only logs events.',
      "Captures are stored for 7 days and then purged automatically. They are never shared outside your school's data silo.",
      'From the Live Screens page you can view the most recent capture per student. Clicking a thumbnail opens the full image with a timestamp and the active URL at time of capture.',
      'If you spot something concerning, use the "Create Incident" button overlaid on the screenshot. This pre-fills the incident form with the student name, timestamp, and a screenshot attachment.',
      'Teachers only see screenshots for students in their assigned classrooms. As school admin you see all students.',
    ],
  },
  {
    id: 'ai-detection',
    category: 'AI & Detection',
    tag: 'ML Pipeline',
    title: 'How the AI Detection Pipeline Works',
    summary:
      'A plain-language explanation of the four-layer ML pipeline — what each layer does, when it fires, and what the confidence scores mean.',
    readTime: '7 min read',
    icon: Brain,
    content: [
      'Every piece of text submitted by a student (search queries, messages, typed content) passes through a four-layer pipeline before a severity decision is made.',
      'Layer 1 — Keyword Triage: A fast keyword and phrase matcher runs first (<5 ms). It fast-paths safe content like greetings and numbers, and surfaces keyword hints for downstream layers. Confidence is capped at 0.79 so it never short-circuits the deeper analysis.',
      'Layer 2 — DeBERTa Model: The primary detector. A fine-tuned DeBERTa transformer model evaluates the text. If it returns confidence ≥ 0.85 and flags the content, Layer 3 is invoked. This layer runs for all non-trivial content and typically takes 50–100 ms.',
      'Layer 3 — Context & Risk: Contextual factors are applied — educational overrides (e.g. a student researching a historical war), self-reporting detection, repeat-offender severity upgrades, and escalation patterns in conversation threads.',
      'Layer 4 — LLM Tiebreaker: Only fires when Layer 2 is ambiguous (confidence 0.50–0.85) or when keyword and model signals conflict. Uses GPT-4o-mini with a circuit breaker and a daily cost cap. Adds 200–500 ms when triggered.',
      'The final result is one of seven detection types (cyberbullying, self_harm, violence, threat, profanity, hate_speech, none) and one of four severity levels (low, medium, high, critical).',
      "You can view per-event ML results on the AI Detection page, including which layers fired and each layer's confidence score.",
    ],
  },
  {
    id: 'user-management',
    category: 'Administration',
    tag: 'Users',
    title: 'Managing Teachers and Students',
    summary:
      'How to add, edit, deactivate, and reassign users — and what each role can and cannot see in the platform.',
    readTime: '4 min read',
    icon: Users,
    content: [
      'There are two user roles you manage as school admin: teacher and student. You cannot modify other school admins — contact your system administrator for that.',
      'Add individual users via the Add User form. For bulk student enrolment use the CSV import. The CSV must include: email, full name, role, and (for students) classroom identifier.',
      'Teachers are scoped to their assigned classrooms. They can view incidents, live activity, and student profiles only for students in their classrooms. They cannot see school-wide analytics.',
      'Students do not have access to the web dashboard. They interact with the platform only through the Chrome extension or Electron app, which surfaces safety prompts and gamification feedback.',
      'Deactivating a user (toggle on the Users page) immediately revokes their session tokens and prevents new logins. Their historical data is retained for audit purposes.',
      'Classroom assignments are managed from the Classrooms page. Drag-and-drop reordering lets you reassign students to different teachers without re-inviting them.',
    ],
  },
];

const CATEGORIES = ['All', ...Array.from(new Set(DOCS.map(d => d.category)))];

export function Documentation({ dm }: { dm: boolean }) {
  const [activeCategory, setActiveCategory] = useState('All');
  const [openId, setOpenId] = useState<string | null>(null);

  const filtered = activeCategory === 'All' ? DOCS : DOCS.filter(d => d.category === activeCategory);
  const openArticle = DOCS.find(d => d.id === openId);

  // ── Glass card ─────────────────────────────────────────────────────────────
  const glassCard = dm
    ? 'bg-slate-900/40 backdrop-blur-md border border-white/[0.1] shadow-lg'
    : 'bg-white/70 backdrop-blur-md border border-slate-200/50 shadow-md';

  // ── Typography ─────────────────────────────────────────────────────────────
  const heading    = dm ? 'text-slate-50'   : 'text-slate-900';
  const subheading = dm ? 'text-slate-400'  : 'text-slate-600';
  const body       = dm ? 'text-slate-300'  : 'text-slate-700';
  const muted      = dm ? 'text-slate-500'  : 'text-slate-500';
  const divider    = dm ? 'border-white/[0.08]' : 'border-slate-200/50';

  // ── Tag pill ───────────────────────────────────────────────────────────────
  const tagPill = dm
    ? '!bg-slate-800/50 !text-slate-300'
    : '!bg-slate-100 !text-slate-600';

  // ── Category chip styles ─────────────────────────────────────────────────
  const activeChip = dm
    ? '!bg-slate-700/80 !text-slate-100 shadow-md backdrop-blur-sm'
    : '!bg-slate-800 !text-white shadow-md';
  const inactiveChip = dm
    ? '!text-slate-400 hover:!bg-slate-800/30 hover:!text-slate-200'
    : '!text-slate-600 hover:!bg-slate-100/80 hover:!text-slate-800';

  // ── Article card hover ─────────────────────────────────────────────────────
  const cardHover = dm ? 'hover:border-white/[0.15] hover:shadow-xl' : 'hover:border-slate-300/60 hover:shadow-lg';

  return (
    <div className="max-w-5xl mx-auto px-6 py-12">

      {/* ── Header section ────────────────────────────────────────────────── */}
      <div className="mb-10">
        <div className="space-y-4 mb-8">
          <h1 className={`text-3xl font-bold tracking-tight ${heading}`}>Documentation</h1>
          <p className={`text-base leading-relaxed ${subheading} max-w-2xl`}>
            Guides and reference material for managing your school.
          </p>
        </div>

        {/* Category chips — only shown on index */}
        {!openArticle && (
          <div className="flex flex-wrap gap-2.5">
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 whitespace-nowrap ${
                  activeCategory === cat ? activeChip : inactiveChip
                }`}
                style={{
                  backgroundColor: activeCategory === cat ? (dm ? 'rgb(51 65 85 / 0.8)' : 'rgb(30 41 59)') : 'transparent',
                  color: activeCategory === cat ? 'white' : (dm ? 'rgb(148 163 184)' : 'rgb(71 85 105)')
                }}
              >
                {cat}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Content section ───────────────────────────────────────────────── */}
      <div>

        {openArticle ? (
          /* ── Article reading view ──────────────────────────────────────── */
          <div className="space-y-6">

            {/* Back */}
            <button
              onClick={() => setOpenId(null)}
              className={`inline-flex items-center gap-2 text-sm font-medium transition-colors mb-4 ${
                dm ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-800'
              }`}
              style={{
                backgroundColor: 'transparent',
                borderColor: dm ? 'rgba(255, 255, 255, 0.1)' : 'rgba(203, 213, 225, 0.5)',
                color: dm ? 'rgb(148 163 184)' : 'rgb(71 85 105)'
              }}
            >
              <ArrowLeft className="h-4 w-4" />
              Back to all articles
            </button>

            {/* Article glass card */}
            <article className={`rounded-2xl ${glassCard} p-10 space-y-6`}>
              {/* Title block */}
              <div className="space-y-4">
                <div className={`flex items-center gap-3 text-sm ${muted}`}>
                  <span className="font-medium">{openArticle.category}</span>
                  <span className="text-slate-300">·</span>
                  <span>{openArticle.tag}</span>
                  <span className="text-slate-300">·</span>
                  <Clock className="h-4 w-4" />
                  <span>{openArticle.readTime}</span>
                </div>
                <h2 className={`text-3xl font-bold leading-tight ${heading}`}>
                  {openArticle.title}
                </h2>
                <p className={`text-base leading-relaxed ${subheading}`}>
                  {openArticle.summary}
                </p>
              </div>

              <hr className={`border-t ${divider}`} />

              {/* Body */}
              <div className="space-y-5">
                {openArticle.content.map((para, i) => (
                  <p key={i} className={`text-base leading-7 ${body}`}>
                    {para}
                  </p>
                ))}
              </div>
            </article>
          </div>

        ) : (
          /* ── Article grid ──────────────────────────────────────────────── */
          <div className="grid gap-5">
            {filtered.map(doc => {
              const Icon = doc.icon;
              return (
                <button
                  key={doc.id}
                  onClick={() => setOpenId(doc.id)}
                  className={`w-full text-left p-6 flex items-start gap-5 group rounded-2xl transition-all duration-300 ${glassCard} ${cardHover}`}
                  style={{
                    backgroundColor: dm ? 'rgba(15, 23, 42, 0.4)' : 'rgba(255, 255, 255, 0.7)',
                    borderColor: dm ? 'rgba(255, 255, 255, 0.1)' : 'rgba(203, 213, 225, 0.5)'
                  }}
                >
                  {/* Icon */}
                  <div
                    className={`mt-1 h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ${
                      dm ? 'bg-white/[0.1]' : 'bg-slate-100'
                    }`}
                  >
                    <Icon className={`h-5 w-5 ${muted}`} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0 space-y-2.5">
                    <div className="flex items-center gap-3">
                      <span className={`text-xs px-3 py-1 rounded-full font-medium ${tagPill}`}>
                        {doc.tag}
                      </span>
                      <span className={`text-xs ${muted}`}>{doc.readTime}</span>
                    </div>
                    <h3 className={`text-lg font-semibold leading-snug ${heading} ${
                      dm ? 'group-hover:text-white' : 'group-hover:text-slate-900'
                    } transition-colors`}>
                      {doc.title}
                    </h3>
                    <p className={`text-sm leading-relaxed ${subheading} line-clamp-2`}>
                      {doc.summary}
                    </p>
                  </div>

                  {/* Arrow */}
                  <ChevronRight
                    className={`h-5 w-5 mt-2 shrink-0 opacity-0 group-hover:opacity-70 transition-all duration-300 group-hover:translate-x-1 ${muted}`}
                  />
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
